// Catalog.ID Client — Recovery Kit Distribution Presets
// ============================================================================
// The distribution patterns of Recovery Kit Addendum §9, offered as the
// selectable presets of §9.4. A preset FIXES the threshold profile (k of n)
// and PREFILLS each sheet's purpose and number of ADDITIONAL printed copies; the
// user may edit every purpose and copy count afterwards, and may of course
// write anything by hand on the printed sheet.
//
// Additional copies (Addendum §9.5): the master document set always
// holds each sheet exactly once, so the owner keeps a first-generation original
// of every type; extras are printed after the set. Several printed copies of
// ONE sheet type carry the identical share and count as ONE sheet toward the
// threshold. They protect availability — the inner circle need not depend on a
// single person — and never lower the quorum. Distinct sheet types for several
// friends would be a different trust model (they could recover WITHOUT the
// notary), chosen deliberately or not at all.
//
// Pure module: no DOM, no crypto — the UI reads it, `createRecoveryKit` takes
// the numbers it produces.

import {
    PROFILE_2_OF_3,
    PROFILE_3_OF_4,
    PROFILE_3_OF_5,
    type RecoveryKitProfile,
} from '../crypto/recovery-kit';

export type RecoveryKitPresetKind = 'personal' | 'organization';

export interface RecoveryKitPresetCard {
    /** Prefilled sheet purpose — who or where the sheet is meant for. */
    label: string;
    /** Prefilled number of ADDITIONAL copies of this sheet TYPE beyond the one
     *  in the master set (0..MAX_CARD_EXTRA_COPIES). */
    extraCopies: number;
}

export interface RecoveryKitPreset {
    id: string;
    kind: RecoveryKitPresetKind;
    /** Option text in the preset picker. */
    title: string;
    /** Why this pattern, in the words the picker shows under it. */
    description: string;
    /** The threshold profile the preset fixes — k and n are not editable. */
    profile: RecoveryKitProfile;
    /** One entry per sheet, index-aligned with share indexes 1..n. */
    cards: RecoveryKitPresetCard[];
}

/**
 * The fewest ADDITIONAL copies of each sheet the registration wizards print
 * (staging test 2026-10-15). The sheets in the printed set stay with it; only
 * copies are handed to the people and places a pattern names, so a sheet with
 * none would have nothing to hand over. The presets keep their own prefills —
 * Settings' kit creator starts from those — and the wizards raise them to this.
 */
export const REGISTRATION_MIN_EXTRA_COPIES = 1;

export const RECOVERY_KIT_PRESETS: RecoveryKitPreset[] = [
    {
        id: 'personal-self-custody',
        kind: 'personal',
        title: 'Self-custody — 2 of 3',
        description: 'Three places you control; any two of them recover the account. '
            + 'Losing one place is survivable, and a thief has to break into two.',
        profile: PROFILE_2_OF_3,
        cards: [
            { label: 'Home', extraCopies: 0 },
            { label: 'Office', extraCopies: 0 },
            { label: 'Storage', extraCopies: 0 },
        ],
    },
    {
        id: 'personal-notary',
        kind: 'personal',
        title: 'Notary and inner circle — 2 of 3 (recommended)',
        description: 'The notary or attorney is the coordinator: the one custodian who knows where '
            + 'the sheets are. The inner circle gets additional identical copies, so recovery does not '
            + 'depend on one person being reachable — their copies together still count as one sheet.',
        profile: PROFILE_2_OF_3,
        cards: [
            { label: 'Personal copy', extraCopies: 0 },
            { label: 'Notary or attorney', extraCopies: 0 },
            { label: 'Inner circle', extraCopies: 2 },
        ],
    },
    {
        id: 'org-small',
        kind: 'organization',
        title: 'Small organization — 2 of 3',
        description: 'Owner, management and an outside professional; any two recover. '
            + 'The outside sheet keeps the organization recoverable if the office is lost.',
        profile: PROFILE_2_OF_3,
        cards: [
            { label: 'Owner', extraCopies: 0 },
            { label: 'Management', extraCopies: 0 },
            { label: 'Notary or lawyer', extraCopies: 0 },
        ],
    },
    {
        id: 'org-medium',
        kind: 'organization',
        title: 'Medium organization — 3 of 4',
        description: 'Two management seats beside the owner and one outside professional; '
            + 'any three recover, so no two people can act alone.',
        profile: PROFILE_3_OF_4,
        cards: [
            { label: 'Owner', extraCopies: 0 },
            { label: 'Management 1', extraCopies: 0 },
            { label: 'Management 2', extraCopies: 0 },
            { label: 'Notary or lawyer', extraCopies: 0 },
        ],
    },
    {
        id: 'org-large',
        kind: 'organization',
        title: 'Large organization — 3 of 5 (recommended)',
        description: 'Five custodians across the organization and two outside professionals; '
            + 'any three recover. Two sheets can be lost outright and the organization is still '
            + 'recoverable, while no two custodians can collude.',
        profile: PROFILE_3_OF_5,
        cards: [
            { label: 'Owner', extraCopies: 0 },
            { label: 'Management 1', extraCopies: 0 },
            { label: 'Management 2', extraCopies: 0 },
            { label: 'Notary or lawyer 1', extraCopies: 0 },
            { label: 'Notary or lawyer 2', extraCopies: 0 },
        ],
    },
];

const DEFAULT_PRESET_ID: Record<RecoveryKitPresetKind, string> = {
    personal: 'personal-notary',
    organization: 'org-large',
};

/** The presets offered for an account of this kind, in picker order. */
export function presetsFor(kind: RecoveryKitPresetKind): RecoveryKitPreset[] {
    return RECOVERY_KIT_PRESETS.filter(p => p.kind === kind);
}

/** The preset a fresh picker starts on. */
export function defaultPresetFor(kind: RecoveryKitPresetKind): RecoveryKitPreset {
    const id = DEFAULT_PRESET_ID[kind];
    const preset = RECOVERY_KIT_PRESETS.find(p => p.id === id);
    if (!preset) throw new Error(`Missing default recovery kit preset '${id}'`);
    return preset;
}

/** Resolve a stored preset id (resume path); null when it no longer exists. */
export function presetById(id: string | undefined | null): RecoveryKitPreset | null {
    if (!id) return null;
    return RECOVERY_KIT_PRESETS.find(p => p.id === id) ?? null;
}

/**
 * The most printed pages a registration's recovery kit of this kind can take:
 * the largest pattern's sheets plus their additional copies, each raised to
 * {@link REGISTRATION_MIN_EXTRA_COPIES}. For the Terms step's page estimate,
 * asked before any pattern is chosen.
 */
export function registrationRecoverySheetCount(kind: RecoveryKitPresetKind): number {
    return Math.max(...presetsFor(kind).map(preset => preset.cards.reduce(
        (pages, card) => pages + 1 + Math.max(REGISTRATION_MIN_EXTRA_COPIES, card.extraCopies), 0)));
}
